import { create } from "zustand";
import { readPageState, writePageState } from "@/features/preferences/page-state";
import { useUserSettingsStore } from "@/features/preferences/user-settings.store";
import {
  fetchGameLibrarySummary,
  fetchGames,
  streamGames,
  type GameLibrarySummary,
  type GameListFilters,
  type GameListItem,
  type GameStreamProgress,
  type ImportProgress,
} from "./games.api";

const GAMES_PAGE_SIZE = 30;
const STREAM_APPEND_BATCH_SIZE = 5;

export type GameCategory = "all" | "rapid" | "blitz" | "bullet" | "daily";
export type GameSortKey = "result" | "opponent" | "rating" | "date" | "opening" | "accuracy";
export type GameSort = { key: GameSortKey; direction: "asc" | "desc" };

export interface GamesFlowState {
  filter?: "all" | "analyzed";
  search: string;
  selectedCategory: GameCategory | null;
  selectedClock: string | null;
  sort: GameSort | null;
}

const initialFlow: GamesFlowState = {
  search: "",
  selectedCategory: null,
  selectedClock: null,
  sort: null,
};

export const isGamesFlow = (value: unknown): value is GamesFlowState => {
  if (!value || typeof value !== "object") return false;
  const flow = value as GamesFlowState;
  return (
    typeof flow.search === "string" &&
    flow.search.length <= 256 &&
    (flow.selectedCategory === null || ["all", "rapid", "blitz", "bullet", "daily"].includes(flow.selectedCategory)) &&
    (flow.selectedClock === null || (typeof flow.selectedClock === "string" && flow.selectedClock.length <= 64)) &&
    (flow.filter === undefined || flow.filter === "all" || flow.filter === "analyzed") &&
    (flow.sort === null ||
      Boolean(
        flow.sort &&
        ["result", "opponent", "rating", "date", "opening", "accuracy"].includes(flow.sort.key) &&
        ["asc", "desc"].includes(flow.sort.direction),
      ))
  );
};

const scheduleFrame = (callback: FrameRequestCallback) => {
  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
    return window.requestAnimationFrame(callback);
  }
  return setTimeout(() => callback(Date.now()), 0);
};

interface GamesState {
  ownerUserId: string | null;
  games: GameListItem[] | null;
  summary: GameLibrarySummary | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMoreGames: boolean;
  streamProgress: GameStreamProgress | null;
  hasLoadedSelection: boolean;
  filters: GameListFilters;
  flow: GamesFlowState;
  importProgress: ImportProgress | null;
  setOwner: (userId: string | null) => void;
  setImportProgress: (progress: ImportProgress | null) => void;
  setFlow: (flow: GamesFlowState) => void;
  markGameAnalyzed: (
    gameId: string,
    result: {
      accuracy: number;
      averageCpl: number;
      blunderCount: number;
      mistakeCount: number;
      analysisDepth: number;
    },
  ) => void;
  isSelectionCached: (filters: GameListFilters) => boolean;
  loadGames: () => Promise<void>;
  loadGamesForSelection: (filters: GameListFilters, force?: boolean) => Promise<void>;
  loadMoreGames: () => Promise<void>;
  loadRemainingGames: () => Promise<void>;
  refreshGames: () => Promise<void>;
  invalidateLibrary: () => void;
}

let inFlightRequest: Promise<void> | null = null;
let backgroundLoadRequest: Promise<void> | null = null;
let selectionStreamController: AbortController | null = null;
let selectionEpoch = 0;
let ownerRevision = 0;

export const useGamesStore = create<GamesState>((set, get) => {
  const requestGames = (replace: boolean, filters = get().filters, refreshSummary = false, limit = GAMES_PAGE_SIZE) => {
    if (inFlightRequest) return inFlightRequest;
    const revision = ownerRevision;

    const offset = replace ? 0 : (get().games?.length ?? 0);
    set(replace ? { games: [], isLoading: true, filters, streamProgress: null } : { isLoadingMore: true });
    const pageRequest = fetchGames(limit, offset, filters);
    const summaryRequest = replace && (refreshSummary || get().summary == null) ? fetchGameLibrarySummary() : undefined;
    const request = Promise.all([pageRequest, summaryRequest])
      .then(([page, summary]) => {
        if (revision !== ownerRevision) return;
        set((state) => ({
          games: replace ? page : [...(state.games ?? []), ...page],
          ...(summary ? { summary } : {}),
          isLoading: false,
          isLoadingMore: false,
          hasMoreGames: page.length === limit,
        }));
      })
      .catch((error: unknown) => {
        if (revision === ownerRevision) set({ isLoading: false, isLoadingMore: false });
        throw error;
      })
      .finally(() => {
        if (inFlightRequest === request) inFlightRequest = null;
      });

    inFlightRequest = request;
    return request;
  };

  return {
    ownerUserId: null,
    games: null,
    summary: null,
    isLoading: true,
    isLoadingMore: false,
    hasMoreGames: true,
    streamProgress: null,
    hasLoadedSelection: false,
    filters: {},
    flow: initialFlow,
    importProgress: null,
    setOwner: (ownerUserId) => {
      if (get().ownerUserId === ownerUserId) return;
      ownerRevision += 1;
      selectionEpoch += 1;
      selectionStreamController?.abort();
      selectionStreamController = null;
      inFlightRequest = null;
      backgroundLoadRequest = null;
      set({
        ownerUserId,
        games: null,
        summary: null,
        isLoading: ownerUserId !== null,
        isLoadingMore: false,
        hasMoreGames: true,
        streamProgress: null,
        hasLoadedSelection: false,
        filters: {},
        flow: (ownerUserId === useUserSettingsStore.getState().ownerUserId
          ? readPageState<GamesFlowState>("games", isGamesFlow)
          : undefined) ?? {
          ...initialFlow,
          selectedCategory: useUserSettingsStore.getState().settings.gamesDefaultFormat,
          selectedClock: useUserSettingsStore.getState().settings.gamesDefaultClock,
          sort: useUserSettingsStore.getState().settings.gamesDefaultSort,
          filter: useUserSettingsStore.getState().settings.gamesDefaultFilter,
        },
        importProgress: null,
      });
    },
    setImportProgress: (importProgress) => set({ importProgress }),
    invalidateLibrary: () => {
      ownerRevision += 1;
      selectionEpoch += 1;
      selectionStreamController?.abort();
      selectionStreamController = null;
      inFlightRequest = null;
      backgroundLoadRequest = null;
      set({
        games: null,
        summary: null,
        isLoading: false,
        isLoadingMore: false,
        hasMoreGames: true,
        streamProgress: null,
        hasLoadedSelection: false,
      });
    },
    setFlow: (flow) => {
      set({ flow });
      writePageState("games", flow);
    },
    markGameAnalyzed: (gameId, result) =>
      set((state) => ({
        games:
          state.games?.map((game) =>
            game.id === gameId
              ? {
                  ...game,
                  analyzed: true,
                  analysisDepth: result.analysisDepth,
                  accuracy: game.chessComAccuracy ?? result.accuracy,
                  engineAccuracy: result.accuracy,
                  averageCpl: result.averageCpl,
                  blunderCount: result.blunderCount,
                  mistakeCount: result.mistakeCount,
                }
              : game,
          ) ?? null,
      })),
    isSelectionCached: (filters) => {
      const state = get();
      return (
        state.hasLoadedSelection &&
        state.games !== null &&
        state.filters.analyzed === filters.analyzed &&
        state.filters.timeControl === filters.timeControl &&
        state.filters.exactTimeControl === filters.exactTimeControl &&
        state.filters.period === filters.period &&
        state.filters.from === filters.from &&
        state.filters.to === filters.to
      );
    },
    loadGames: async () => {
      if (get().games !== null) return;
      if (inFlightRequest) return inFlightRequest;

      const revision = ownerRevision;
      set({ isLoading: true, streamProgress: null });
      const request = fetchGameLibrarySummary()
        .then((summary) => {
          if (revision !== ownerRevision) return;
          set({
            games: [],
            summary,
            isLoading: false,
            isLoadingMore: false,
            hasMoreGames: false,
            hasLoadedSelection: false,
          });
        })
        .catch((error: unknown) => {
          if (revision === ownerRevision) set({ isLoading: false });
          throw error;
        })
        .finally(() => {
          if (inFlightRequest === request) inFlightRequest = null;
        });
      inFlightRequest = request;
      return request;
    },
    loadGamesForSelection: async (filters, force = false) => {
      const revision = ownerRevision;
      if (inFlightRequest) await inFlightRequest;
      if (revision !== ownerRevision) return;
      if (!force && get().isSelectionCached(filters)) return;
      selectionStreamController?.abort();
      const controller = new AbortController();
      selectionStreamController = controller;
      const epoch = ++selectionEpoch;

      if (force || get().summary == null) {
        void fetchGameLibrarySummary()
          .then((summary) => {
            if (epoch === selectionEpoch) set({ summary });
          })
          .catch(() => undefined);
      }

      set({
        games: [],
        filters,
        hasMoreGames: false,
        isLoading: true,
        isLoadingMore: false,
        streamProgress: null,
        hasLoadedSelection: false,
      });

      const appendStreamChunk = (chunk: GameListItem[], progress: GameStreamProgress) =>
        new Promise<void>((resolve) => {
          let offset = 0;
          const appendNextSlice = () => {
            if (epoch !== selectionEpoch) {
              resolve();
              return;
            }
            const gamesSlice = chunk.slice(offset, offset + STREAM_APPEND_BATCH_SIZE);
            offset += gamesSlice.length;

            set((state) => {
              const previousLoaded = state.streamProgress?.loaded ?? Math.max(0, progress.loaded - chunk.length);
              return {
                games: [...(state.games ?? []), ...gamesSlice],
                streamProgress: {
                  loaded: Math.min(progress.loaded, previousLoaded + gamesSlice.length),
                  total: progress.total,
                },
              };
            });

            if (offset < chunk.length) {
              scheduleFrame(appendNextSlice);
            } else {
              resolve();
            }
          };

          if (chunk.length === 0) {
            set({ streamProgress: progress });
            resolve();
            return;
          }
          scheduleFrame(appendNextSlice);
        });

      return streamGames(
        filters,
        {
          onProgress: (streamProgress) => {
            if (epoch === selectionEpoch) set({ streamProgress });
          },
          onGames: appendStreamChunk,
        },
        controller.signal,
      )
        .then(() => {
          if (epoch === selectionEpoch) set({ hasLoadedSelection: true });
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          throw error;
        })
        .finally(() => {
          if (epoch === selectionEpoch) {
            set({ isLoading: false });
            selectionStreamController = null;
          }
        });
    },
    loadMoreGames: async () => {
      if (!get().hasMoreGames) return;
      return requestGames(false);
    },
    loadRemainingGames: async () => {
      if (backgroundLoadRequest) return backgroundLoadRequest;
      const revision = ownerRevision;
      const request = (async () => {
        while (revision === ownerRevision && get().hasMoreGames) {
          await requestGames(false);
        }
      })().finally(() => {
        if (backgroundLoadRequest === request) backgroundLoadRequest = null;
      });
      backgroundLoadRequest = request;
      return request;
    },
    refreshGames: async () => {
      if (get().streamProgress) {
        return get().loadGamesForSelection(get().filters, true);
      }
      return requestGames(true, get().filters, true);
    },
  };
});
